/**
 * Last Workout Data — fetches the most recent performance for an exercise
 * with in-memory caching to avoid re-queries when switching between exercises.
 */
import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';

const log = createLogger('LastWorkout');

export interface LastSetData {
  weight: number;
  reps: number;
}

export interface LastPerformance {
  date: string; // formatted like "Feb 18"
  sets: LastSetData[];
  units: string; // the units this workout was logged in
}

// In-memory cache: key = `${userId}::${exerciseName.toLowerCase()}`
const cache = new Map<string, { data: LastPerformance | null; ts: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function cacheKey(userId: string, exerciseName: string): string {
  return `${userId}::${exerciseName.toLowerCase()}`;
}

export function clearLastPerformanceCache(): void {
  cache.clear();
}

export async function getLastPerformance(
  userId: string,
  exerciseName: string,
): Promise<LastPerformance | null> {
  const key = cacheKey(userId, exerciseName);
  const cached = cache.get(key);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
    return cached.data;
  }

  try {
    // Query most recent workout containing this exercise
    const { data: workouts, error } = await supabase
      .from('workouts')
      .select('performed_at, payload')
      .eq('user_id', userId)
      .order('performed_at', { ascending: false })
      .limit(20); // scan recent workouts to find one with this exercise

    if (error || !workouts || workouts.length === 0) {
      cache.set(key, { data: null, ts: Date.now() });
      return null;
    }

    const nameLower = exerciseName.toLowerCase();

    for (const w of workouts) {
      const payload = w.payload as any;
      if (!payload?.exercises) continue;

      const match = (payload.exercises as any[]).find(
        (ex: any) => (ex.name ?? '').toLowerCase() === nameLower,
      );

      if (match && match.sets && match.sets.length > 0) {
        const d = new Date(w.performed_at);
        const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const units = payload.units || 'lbs';
        const sets: LastSetData[] = match.sets.map((s: any) => ({
          weight: s.weight ?? 0,
          reps: s.reps ?? 0,
        }));

        const result: LastPerformance = { date, sets, units };
        cache.set(key, { data: result, ts: Date.now() });
        return result;
      }
    }

    cache.set(key, { data: null, ts: Date.now() });
    return null;
  } catch (err) {
    log.error('getLastPerformance error', err);
    return null;
  }
}
